/**
 * Field-level validators for `path_PathUpdateResource`.
 * Split out of `path.ts` to keep each file under the 150-line limit.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  AUTO_OPTIMIZATION_MODE_VALUES,
  CALCULATION_METHOD_VALUES,
  SORT_ORDER_VALUES,
} from '../enums';
import {
  joinPath,
  pushError,
  requireString,
  type ValidationCtx,
} from '../primitives';

/**
 * Voluum contract for automation fields:
 *  - `autoOptimized: true`  → `autoOptimizationMode` + `calculationMethod` required.
 *  - `autoOptimized: false` → both fields MUST be absent.
 */
export function validateAutomationFields(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  if (raw.autoOptimized === true) {
    requireString(
      ctx,
      raw,
      'autoOptimizationMode',
      basePath,
      AUTO_OPTIMIZATION_MODE_VALUES,
    );
    if (raw.calculationMethod !== undefined) {
      requireString(
        ctx,
        raw,
        'calculationMethod',
        basePath,
        CALCULATION_METHOD_VALUES,
      );
    }
    return;
  }
  if (raw.autoOptimized === false) {
    rejectAutomationFields(ctx, raw, basePath);
  }
}

function rejectAutomationFields(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  if (raw.autoOptimizationMode !== undefined) {
    pushError(
      ctx,
      joinPath(basePath, 'autoOptimizationMode'),
      'improper_automation',
      'Must be absent when autoOptimized=false',
    );
  }
  if (raw.calculationMethod !== undefined) {
    pushError(
      ctx,
      joinPath(basePath, 'calculationMethod'),
      'improper_automation',
      'Must be absent when autoOptimized=false',
    );
  }
}

export function validatePathOptional(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  if (raw.smartRotation !== undefined && typeof raw.smartRotation !== 'boolean') {
    pushError(
      ctx,
      joinPath(basePath, 'smartRotation'),
      'wrong_type',
      'Expected boolean',
    );
  }
  if (raw.id !== undefined && typeof raw.id !== 'string') {
    pushError(ctx, joinPath(basePath, 'id'), 'wrong_type', 'Expected string');
  }
  for (const f of ['landersDisplaySortOrder', 'offersDisplaySortOrder'] as const) {
    if (raw[f] !== undefined) {
      requireString(ctx, raw, f, basePath, SORT_ORDER_VALUES);
    }
  }
}
